import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {TextureType} from "@cm/material-nodes"
import {CM_TO_INCH, materialMapsExporterLogger} from "#material-maps-exporter/core/types"
import type {
    Resolution,
    EncodedConvertedNode,
    ImageMapInfo,
    InputMapsInfo,
    InputMapsNodes,
    MapsNodesForCanonicalTextureTypes,
} from "#material-maps-exporter/core/types"
import {
    encodeConvertedNode,
    getImageMapInfos,
    getMapNodeOrProvideFallback,
    imageProcessingGraphForInputMaps,
    mix,
    convertNormal,
    provideFallbackMap,
    alphaFromMaskOrTransmission,
} from "#material-maps-exporter/core/image-processing-graph"
import type {ConversionConfig, ConvertResult} from "#material-maps-exporter/targets/pbr/types"
import {getAveragePhysicalSize} from "#material-maps-exporter/core/gather-material-data"

function ansiotropyRotationAndStrengthToVector(rotation: Nodes.ImageNode, strength: Nodes.ImageNode, valZ = 0.5): Nodes.ImageNode {
    const mag = Nodes.math("*", Nodes.math("-", strength, 0.5), 2.0)
    const rot = Nodes.math("*", rotation, 2 * 2 * Math.PI)
    let vecX = Nodes.math("*", Nodes.math("cos", rot), mag)
    let vecY = Nodes.math("*", Nodes.math("sin", rot), Nodes.math("*", mag, -1))
    vecX = Nodes.math("+", Nodes.math("*", vecX, 0.5), 0.5)
    vecY = Nodes.math("+", Nodes.math("*", vecY, 0.5), 0.5)
    const vecZ = Nodes.math("constLike", mag, valZ)

    return Nodes.combineChannels([vecX, vecY, vecZ], "RGB")
}

function anisotropyVectorToRotationAndStrength(anisoVec: Nodes.ImageNode): {
    rotation: Nodes.ImageNode
    strength: Nodes.ImageNode
} {
    const vecX = Nodes.math("*", Nodes.math("-", Nodes.extractChannel(anisoVec, 0), 0.5), 2.0)
    const vecY = Nodes.math("*", Nodes.math("-", Nodes.extractChannel(anisoVec, 1), 0.5), 2.0)
    let rot = Nodes.math("/", Nodes.math("atan2", Nodes.math("*", vecY, -1), vecX), 2 * 2 * Math.PI)

    rot = Nodes.math("+", rot, Nodes.math("*", Nodes.math("<", rot, 0.0), 0.5))
    rot = Nodes.math("+", rot, Nodes.math("*", Nodes.math("<", rot, 0.0), 0.5))
    rot = Nodes.math("-", rot, Nodes.math("*", Nodes.math(">", rot, 0.5), 0.5))
    rot = Nodes.math("-", rot, Nodes.math("*", Nodes.math(">", rot, 0.5), 0.5))
    const strength = Nodes.math("sqrt", Nodes.math("+", Nodes.math("square", vecX), Nodes.math("square", vecY)))

    return {rotation: rot, strength: strength}
}

export function estimateCommonSize(imageMapsInfo: ImageMapInfo[], resolution: Resolution) {
    let majoritySize: [number, number] | undefined = undefined
    let majorityCount = 0
    const sizeCount = new Map<string, number>()

    for (const imageMapInfo of imageMapsInfo) {
        const sizeStr = `${imageMapInfo.width}-${imageMapInfo.height}`
        let count = sizeCount.get(sizeStr)
        count = typeof count === "undefined" ? 1 : count + 1
        sizeCount.set(sizeStr, count)
        if (count > majorityCount) {
            majorityCount = count
            majoritySize = [imageMapInfo.width, imageMapInfo.height]
        }
    }

    const avgPhyiscalSize = (() => {
        const {width, height, inhomogeneousPhysicalSizesDetected} = getAveragePhysicalSize(imageMapsInfo)
        if (inhomogeneousPhysicalSizesDetected) {
            materialMapsExporterLogger?.debug(`Warning: Inhomogeneous physical sizes detected for export, DPI information will not be set`)
            return undefined
        }
        return {width, height}
    })()

    const targetDpi = (() => {
        switch (resolution) {
            case "original":
                return undefined
            case "dpi72":
                return 72
        }
    })()

    const retValue = ((): {
        resX: number
        resY: number
        dpi?: number
    } => {
        if (majoritySize) {
            if (avgPhyiscalSize === undefined) return {resX: majoritySize[0], resY: majoritySize[1], dpi: undefined}

            const getCurrentDpi = (majoritySize: [number, number]) => {
                const dpiX = majoritySize[0] / (avgPhyiscalSize.width * CM_TO_INCH)
                const dpiY = majoritySize[1] / (avgPhyiscalSize.height * CM_TO_INCH)
                const dpi = (dpiX + dpiY) / 2
                return dpi
            }

            if (targetDpi) {
                const currentDpi = getCurrentDpi(majoritySize)
                const scale = targetDpi / currentDpi
                majoritySize = [Math.round(majoritySize[0] * scale), Math.round(majoritySize[1] * scale)]
            }

            return {
                resX: majoritySize[0],
                resY: majoritySize[1],
                dpi: targetDpi !== undefined ? targetDpi : getCurrentDpi(majoritySize),
            }
        } else if (targetDpi) throw Error(`No majority size found, cannot scale to target DPI ${targetDpi}`)
        else return {resX: 256, resY: 256}
    })()

    return retValue
}

function ensureCommonSize(mapsInfo: InputMapsInfo, mapsNodes: InputMapsNodes, resolution: Resolution) {
    const imageMapsInfo = getImageMapInfos(mapsInfo)
    const retValue = estimateCommonSize(imageMapsInfo, resolution)
    const {resX, resY} = retValue

    for (const imageMapInfo of imageMapsInfo) {
        if (resX !== imageMapInfo.width || resY !== imageMapInfo.height)
            mapsNodes[imageMapInfo.textureType] = Nodes.resize(mapsNodes[imageMapInfo.textureType], resX, resY)
    }

    for (const type of Object.keys(mapsInfo)) {
        const mapInfo = mapsInfo[type]
        if (mapInfo.data.type === "ShaderNodeRGB") {
            mapsNodes[type] = Nodes.resize(mapsNodes[type], resX, resY)
        }
    }

    return retValue
}

function convertToCanonicalMaps(
    mapsNodes: InputMapsNodes,
    mapsInfo: InputMapsInfo,
    fallbackResX: number,
    fallbackResY: number,
): MapsNodesForCanonicalTextureTypes {
    function getSpecularAndMetalness(mapsNodes: InputMapsNodes): {
        specular: Nodes.ImageNode
        metalness: Nodes.ImageNode
    } {
        const types = Object.keys(mapsNodes)
        if (types.includes(TextureType.SpecularStrength) && types.includes(TextureType.Metalness)) {
            return {specular: mapsNodes[TextureType.SpecularStrength], metalness: mapsNodes[TextureType.Metalness]}
        } else if (types.includes(TextureType.F0)) {
            const f0 = mapsNodes[TextureType.F0]
            return {specular: Nodes.extractChannel(f0, 0), metalness: Nodes.extractChannel(f0, 1)}
        } else {
            return {
                specular: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.SpecularStrength, 0.04, "linear"),
                metalness: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Metalness, 0, "linear"),
            }
        }
    }

    function getVectorAnisotropy(mapsNodes: InputMapsNodes): Nodes.ImageNode {
        const types = Object.keys(mapsNodes)
        if (types.includes(TextureType.AnisotropyRotation) && types.includes(TextureType.AnisotropyStrength)) {
            return ansiotropyRotationAndStrengthToVector(mapsNodes[TextureType.AnisotropyRotation], mapsNodes[TextureType.AnisotropyStrength])
        } else if (types.includes(TextureType.Anisotropy)) {
            return mapsNodes[TextureType.Anisotropy]
        } else {
            return getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Anisotropy, [0.5, 0.5, 0], "linear")
        }
    }

    function getAlphaAndTransmission(mapsNodes: InputMapsNodes): {
        alpha: Nodes.ImageNode
        transmission: Nodes.ImageNode
    } {
        return {
            alpha: alphaFromMaskOrTransmission(mapsNodes, mapsInfo) ?? provideFallbackMap(fallbackResX, fallbackResY, "linear", 1),
            transmission: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Transmission, [0, 0, 0], "linear"),
        }
    }

    const {specular, metalness} = getSpecularAndMetalness(mapsNodes)
    const anisotropy = getVectorAnisotropy(mapsNodes)
    const {alpha, transmission} = getAlphaAndTransmission(mapsNodes)

    return {
        diffuse: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Diffuse, [0, 0, 0], "linear"),
        normal: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Normal, [0.5, 0.5, 1.0], "linear"),
        roughness: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Roughness, 1, "linear"),
        "specular-strength": specular,
        metalness,
        anisotropy,
        displacement: getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Displacement, 0, "linear"),
        alpha,
        transmission,
    }
}

function getMetalnessRoughnessMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined) {
    const diffuse = canonicalMapsNodes["diffuse"]
    const metalness = Nodes.math("clip01", canonicalMapsNodes["metalness"])
    const roughness = Nodes.math("clip01", canonicalMapsNodes["roughness"])
    const specular = Nodes.math("clip01", canonicalMapsNodes["specular-strength"])

    return {
        diffuse: encodeConvertedNode(diffuse, config.format, dpi, true, false),
        specular: encodeConvertedNode(specular, config.format, dpi),
        metalness: encodeConvertedNode(metalness, config.format, dpi),
        roughness: encodeConvertedNode(roughness, config.format, dpi),
    }
}

function getSpecularGlossinessMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined) {
    const specular = Nodes.math("clip01", canonicalMapsNodes["specular-strength"])
    const metalness = Nodes.math("clip01", canonicalMapsNodes["metalness"])
    const ones = Nodes.math("constLike", specular, 1.0)

    const specular_ms = Nodes.math("*", Nodes.math("max", specular, 0), 0.08)
    const f0_scalar = Nodes.math("min", Nodes.math("max", mix(specular_ms, ones, metalness), 1e-6), 1)
    const f0_s_ = Nodes.math("sqrt", f0_scalar)
    const ior = Nodes.math("/", Nodes.math("-", ones, f0_s_), Nodes.math("+", ones, f0_s_))

    const baseColor = canonicalMapsNodes["diffuse"]
    const diffuse = mix(baseColor, 0, metalness)
    const reflect = Nodes.math("/", mix(specular_ms, baseColor, metalness), f0_scalar)
    const gloss = Nodes.math("-", ones, canonicalMapsNodes["roughness"])

    return {
        diffuse: encodeConvertedNode(diffuse, config.format, dpi, true, false),
        reflect: encodeConvertedNode(reflect, config.format, dpi, true, false),
        gloss: encodeConvertedNode(gloss, config.format, dpi),
        "fresnel-ior": encodeConvertedNode(ior, config.format, dpi),
    }
}

function getWorkflowMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined) {
    if (config.workflow === "metalnessRoughness") return getMetalnessRoughnessMaps(canonicalMapsNodes, config, dpi)
    else if (config.workflow === "specularGlossiness") return getSpecularGlossinessMaps(canonicalMapsNodes, config, dpi)
    else throw Error(`Unsupported workflow: ${config.workflow}`)
}

function getAlphaAndTransmissionMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined) {
    return {
        transmission: encodeConvertedNode(canonicalMapsNodes["transmission"], config.format, dpi, true, false),
        alpha: encodeConvertedNode(canonicalMapsNodes["alpha"], config.format, dpi),
    }
}

function convertToCoronaMaps(
    canonicalMapsNodes: MapsNodesForCanonicalTextureTypes,
    config: ConversionConfig,
    dpi: number | undefined,
): ConvertResult<"corona"> {
    const vector = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])
    vector.strength = Nodes.math("+", Nodes.math("*", vector.strength, 0.5), 0.5) //Map to 0.5..1.0 range

    const normal = convertNormal(canonicalMapsNodes["normal"], config.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, config, dpi),
        normal: encodeConvertedNode(normal, config.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(vector.strength, config.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(vector.rotation, config.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], config.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, config, dpi),
    }
}

function convertToVrayMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined): ConvertResult<"vray"> {
    const {rotation, strength} = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])

    const normal = convertNormal(canonicalMapsNodes["normal"], config.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, config, dpi),
        normal: encodeConvertedNode(normal, config.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(strength, config.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(rotation, config.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], config.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, config, dpi),
    }
}

function convertToCyclesMaps(
    canonicalMapsNodes: MapsNodesForCanonicalTextureTypes,
    config: ConversionConfig,
    dpi: number | undefined,
): ConvertResult<"cycles"> {
    const vector = anisotropyVectorToRotationAndStrength(canonicalMapsNodes["anisotropy"])
    vector.rotation = Nodes.math("+", Nodes.math("*", vector.rotation, -1), 1) // CCW rotation

    const normal = convertNormal(canonicalMapsNodes["normal"], config.normalY, "y+up")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, config, dpi),
        normal: encodeConvertedNode(normal, config.format, dpi, false, false),
        "anisotropy-strength": encodeConvertedNode(vector.strength, config.format, dpi),
        "anisotropy-rotation": encodeConvertedNode(vector.rotation, config.format, dpi),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], config.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, config, dpi),
    }
}

function convertToCmMaps(canonicalMapsNodes: MapsNodesForCanonicalTextureTypes, config: ConversionConfig, dpi: number | undefined): ConvertResult<"cm"> {
    const normal = convertNormal(canonicalMapsNodes["normal"], config.normalY, "y+down")

    return {
        ...getWorkflowMaps(canonicalMapsNodes, config, dpi),
        normal: encodeConvertedNode(normal, config.format, dpi, false, false),
        anisotropy: encodeConvertedNode(canonicalMapsNodes["anisotropy"], config.format, dpi, false, false),
        displacement: encodeConvertedNode(canonicalMapsNodes["displacement"], config.format, dpi, false, true),
        ...getAlphaAndTransmissionMaps(canonicalMapsNodes, config, dpi),
    }
}

export function imageProcessingGraphForPbrConversionConfig(
    config: ConversionConfig,
    mapsInfo: InputMapsInfo,
): Nodes.Struct<Record<string, EncodedConvertedNode>> {
    const mapsNodes = imageProcessingGraphForInputMaps(mapsInfo)
    const {resX, resY, dpi} = ensureCommonSize(mapsInfo, mapsNodes, config.resolution)
    const canonicalMapsNodes = convertToCanonicalMaps(mapsNodes, mapsInfo, resX, resY)

    let conversionFn: typeof convertToCoronaMaps | typeof convertToVrayMaps | typeof convertToCyclesMaps | typeof convertToCmMaps
    switch (config.engine) {
        case "corona":
            conversionFn = convertToCoronaMaps
            break
        case "vray":
            conversionFn = convertToVrayMaps
            break
        case "cycles":
            conversionFn = convertToCyclesMaps
            break
        case "cm":
            conversionFn = convertToCmMaps
            break
        default:
            throw Error(`Unsupported engine: ${config.engine}`)
    }

    return Nodes.struct(conversionFn(canonicalMapsNodes, config, dpi))
}
